import * as debug from 'debug';
import * as _ from 'lodash';
import { Injectable } from '@angular/core';
import { EventEmitter } from '../../includes/eventEmitter';
import { UserProvider } from '../user/user';
import { ToolsProvider } from '../tools/tools';
import { CrudApiProvider } from '../crud-api/crud-api';
import { ToastController, AlertController } from 'ionic-angular';

@Injectable()
export class MessagesProvider extends EventEmitter {
  log = debug('app:MessagesProvider');

  constructor(
    public user: UserProvider,
    public tools: ToolsProvider,
    public crudApi: CrudApiProvider,
    public toastCtrl: ToastController,
    public alertCtrl: AlertController
  ) {
    super();
  }

  getConversations(): Promise<any> {
    return this.crudApi
      .get('/api/conversations', {
        sortBy: 'dates.updated:desc',
      })
      .then(res => {
        return Promise.resolve(res.data || []);
      })
      .catch(e => {
        this.log('error getting conversations', e);
        const toast = this.toastCtrl.create({
          message:
            'An error occurred while retrieving your conversations. Please check your internet connection and try again. If the issue continues, please contact us for help.',
          position: 'top',
          duration: 5000,
        });
        toast.present();
        return Promise.reject('error getting conversations');
      });
  }

  async getPublicRooms(): Promise<any> {
    return this.crudApi
      .get('/api/conversations', {
        sortBy: 'dates.updated:desc',
      })
      .then(res => {
        console.log('PUBLIC Chat res == ', res);
        return Promise.resolve(res.data || []);
      })
      .catch(e => {
        this.log('error getting conversations', e);
        const toast = this.toastCtrl.create({
          message:
            'An error occurred while retrieving your conversations. Please check your internet connection and try again. If the issue continues, please contact us for help.',
          position: 'top',
          duration: 5000,
        });
        toast.present();
        return Promise.reject('error getting conversations');
      });
  }

  createNewConversation(friendIds: any[], name = '', source?): Promise<any> {
    return this.doesConvoExist(friendIds).then(convo => {
      if (convo) {
        return Promise.resolve(convo);
      } else {
        return this.crudApi
          .post('/api/conversations', {
            userIds: friendIds,
            name,
            source,
          })
          .then(res => {
            this.log('new convo', res.data);
            this.tools.logAnalyticsEvent('trackCustom', 'conversation-create', {
              user: _.get(this.user, 'info._id'),
            });
            return Promise.resolve(res.data);
          })
          .catch(e => {
            this.log('error creating new conversation', e);
            this.tools.logAnalyticsEvent(
              'trackCustom',
              'conversation-create-error',
              {
                user: _.get(this.user, 'info._id'),
              }
            );
            const toast = this.toastCtrl.create({
              message:
                'An error occurred while creating a new conversation. Please check your internet connection and try again. If the issue continues, please contact us for help.',
              position: 'top',
              duration: 5000,
            });
            toast.present();
          });
      }
    });
  }

  doesConvoExist(friend): Promise<any> {
    let userId = _.get(this.user, 'info._id');
    let idArray = _.cloneDeep(friend);
    idArray.push(userId);

    return this.getConversations().then(convos => {
      let existingConvo;
      let conversations = _.cloneDeep(convos);
      let matchingConvos = _.filter(conversations, c => {
        let users = _.cloneDeep(_.get(c, 'users') || []);

        users = _.map(users, u => String(u._id));

        return _.isEqual(_.sortBy(users), _.sortBy(idArray));
      });

      matchingConvos = _.sortBy(matchingConvos, 'dates.created').reverse();
      existingConvo = _.first(matchingConvos) || false;

      return Promise.resolve(existingConvo);
    });
  }

  getConversation(id): Promise<any> {
    return this.crudApi
      .get(`/api/conversations/${id}`)
      .then(res => {
        return Promise.resolve(res.data);
      })
      .catch(e => {
        this.log('error getting existing convo: ', e);
        this.tools.logAnalyticsEvent('trackCustom', 'conversation-get-error', {
          user: _.get(this.user, 'info._id'),
        });
        // toast error alert is inside of the individual function calls since
        // this function can be called repeatedly to refresh a confo

        return Promise.reject(e);
      });
  }

  sendMessage(messageObj): Promise<any> {
    return this.crudApi
      .post('/api/messages', messageObj)
      .then(response => {
        this.tools.logEvent('directMessage');
        this.tools.logAnalyticsEvent('trackCustom', 'message-sent', {
          user: _.get(this.user, 'info._id'),
        });
        return Promise.resolve(response.data);
      })
      .catch(e => {
        this.log('error posting new message', e);
        const prompt = this.alertCtrl.create({
          title: "Oh no, we're sorry!",
          message:
            'An error occurred while sending your message. Please check your connection and try again. If the issue continues, please contact us for help.',
          buttons: [
            {
              text: 'OK',
              role: 'cancel',
              handler: data => {
                console.warn('OK clicked');
              },
            },
          ],
        });
        prompt.present();
        this.tools.logAnalyticsEvent('trackCustom', 'message-send-error', {
          user: _.get(this.user, 'info._id'),
        });
        return Promise.reject(e);
      });
  }

  addMoreMembersToGroup(friendsList, conversation): Promise<any> {
    let friendsIdArray = [];
    let convoId = _.get(conversation, '_id');

    _.each(friendsList, friend => {
      friendsIdArray.push(friend._id);
    });

    return this.crudApi
      .post(`/api/conversations/${convoId}/users`, {
        userIds: friendsIdArray,
      })
      .then(response => {
        this.tools.logAnalyticsEvent('trackCustom', 'message-add-members', {
          user: _.get(this.user, 'info._id'),
        });
        return Promise.resolve(response.data);
      })
      .catch(e => {
        this.log('error adding member to group', e);
        const toast = this.toastCtrl.create({
          message:
            'An error occurred while adding a member to your group. Please check your connection and try again. If the issue continues, please contact us for help.',
          position: 'top',
          duration: 5000,
        });
        toast.present();
        this.tools.logAnalyticsEvent(
          'trackCustom',
          'message-add-members-error',
          {
            user: _.get(this.user, 'info._id'),
          }
        );
      });
  }

  removeMemberFromGroup(userId, convoId): Promise<any> {
    return this.crudApi
      .delete(`/api/conversations/${convoId}/users/${userId}`)
      .then(response => {
        this.tools.logAnalyticsEvent('trackCustom', 'message-remove-member', {
          user: _.get(this.user, 'info._id'),
        });
        return Promise.resolve(response.data);
      })
      .catch(e => {
        this.log('error removing member from group', e);
        const toast = this.toastCtrl.create({
          message:
            'An error occurred while removing member from group. Please check your connection and try again. If the issue continues, please contact us for help.',
          position: 'top',
          duration: 5000,
        });
        toast.present();
        this.tools.logAnalyticsEvent(
          'trackCustom',
          'message-remove-member-error',
          {
            user: _.get(this.user, 'info._id'),
          }
        );
      });
  }

  saveGroupName(newName, convoId): Promise<any> {
    return this.crudApi
      .put(`/api/conversations/${convoId}`, {
        name: newName,
      })
      .then(response => {
        return Promise.resolve(response.data);
      })
      .catch(e => {
        this.log('deleting conversation', e);
        const toast = this.toastCtrl.create({
          message:
            'An error occurred while sending your message. Please check your connection and try again. If the issue continues, please contact us for help.',
          position: 'top',
          duration: 500,
        });
        toast.present();
      });
  }

  turnOnDND(userId, convoId): Promise<any> {
    let userArray = [];
    userArray.push(userId);

    return this.crudApi
      .post(`/api/conversations/${convoId}/dndUsers`, {
        userIds: userArray,
      })
      .then(response => {
        this.tools.logAnalyticsEvent('trackCustom', 'message-turnOnDnD', {
          user: _.get(this.user, 'info._id'),
        });
        return Promise.resolve(response.data);
      })
      .catch(e => {
        this.log('error adding to dnd array', e);
        const toast = this.toastCtrl.create({
          message:
            'An error occurred while turning off notifications. Please check your connection and try again. If the issue continues, please contact us for help.',
          position: 'top',
          duration: 500,
        });
        toast.present();
        this.tools.logAnalyticsEvent('trackCustom', 'message-turnOnDnD-error', {
          user: _.get(this.user, 'info._id'),
        });
      });
  }

  turnOffDND(userId, convoId): Promise<any> {
    return this.crudApi
      .delete(`/api/conversations/${convoId}/dndUsers/${userId}`)
      .then(response => {
        this.tools.logAnalyticsEvent('trackCustom', 'message-turnOffDnD', {
          user: _.get(this.user, 'info._id'),
        });
        return Promise.resolve(response.data);
      })
      .catch(e => {
        this.log('error removing from dnd array', e);
        const toast = this.toastCtrl.create({
          message:
            'An error occurred while turning on notifications. Please check your connection and try again. If the issue continues, please contact us for help.',
          position: 'top',
          duration: 500,
        });
        toast.present();
        this.tools.logAnalyticsEvent(
          'trackCustom',
          'message-turnOffDnD-error',
          {
            user: _.get(this.user, 'info._id'),
          }
        );
      });
  }
}



// WEBPACK FOOTER //
// ./src/providers/messages/messages.ts